import { pipe } from './function';
import { flap as flap_ } from './Functor';
import * as O from './Option';
import * as RR from './ReadonlyRecord'; // -------------------------------------------------------------------------------------
// model
// -------------------------------------------------------------------------------------

/**
 * Calculate the number of key/value pairs in a `Record`.
 *
 * @since 2.0.0
 */

export var size = RR.size;
/**
 * Test whether a `Record` is empty.
 *
 * @since 2.0.0
 */

export var isEmpty = RR.isEmpty;
/**
 * @since 2.0.0
 */

export var keys = RR.keys;
/**
 * Map a `Record` into an `Array`.
 *
 * @example
 * import { collect } from 'fp-ts/Record'
 *
 * const x: { readonly a: string, readonly b: boolean } = { a: 'c', b: false }
 * assert.deepStrictEqual(
 *   collect((key, val) => ({ key: key, value: val }))(x),
 *   [{ key: 'a', value: 'c' }, { key: 'b', value: false }]
 * )
 *
 * @since 2.0.0
 */

export var collect = function (f) {
  return function (r) {
    var out = [];

    for (var _i = 0, _a = keys(r); _i < _a.length; _i++) {
      var key = _a[_i];
      out.push(f(key, r[key]));
    }

    return out;
  };
};
/**
 * Get a sorted `Array` of the key/value pairs contained in a `Record`.
 *
 * @since 2.0.0
 */

export var toArray = /*#__PURE__*/collect(function (k, a) {
  return [k, a];
});
export function toUnfoldable(U) {
  return function (r) {
    var sas = toArray(r);
    var len = sas.length;
    return U.unfold(0, function (b) {
      return b < len ? O.some([sas[b], b + 1]) : O.none;
    });
  };
}
/**
 * Insert or replace a key/value pair in a `Record`.
 *
 * @category combinators
 * @since 2.10.0
 */

export var upsertAt = RR.upsertAt;
/**
 * Test whether or not a key exists in a `Record`.
 *
 * Note. This function is not pipeable because is a custom type guard.
 *
 * @since 2.10.0
 */

export var has = RR.has;
var _hasOwnProperty = Object.prototype.hasOwnProperty;
export function deleteAt(k) {
  return function (r) {
    if (!_hasOwnProperty.call(r, k)) {
      return r;
    }

    var out = Object.assign({}, r);
    delete out[k];
    return out;
  };
}
/**
 * @since 2.0.0
 */

export var updateAt = function (k, a) {
  return modifyAt(k, function () {
    return a;
  });
};
/**
 * @since 2.0.0
 */

export var modifyAt = function (k, f) {
  return function (r) {
    if (!has(k, r)) {
      return O.none;
    }

    var out = Object.assign({}, r);
    out[k] = f(r[k]);
    return O.some(out);
  };
};
export function pop(k) {
  var deleteAtk = deleteAt(k);
  return function (r) {
    var oa = lookup(k, r);
    return O.isNone(oa) ? O.none : O.some([oa.value, deleteAtk(r)]);
  };
} // TODO: remove non-curried overloading in v3

/**
 * Test whether one `Record` contains all of the keys and values contained in another `Record`.
 *
 * @since 2.0.0
 */

export var isSubrecord = RR.isSubrecord; // TODO: remove non-curried overloading in v3

/**
 * Lookup the value for a key in a `Record`.
 *
 * @since 2.0.0
 */

export var lookup = RR.lookup;
/**
 * Map a `Record` passing the keys to the iterating function.
 *
 * @since 2.0.0
 */

export var mapWithIndex = RR.mapWithIndex;
/**
 * Map a `Record` passing the values to the iterating function.
 *
 * @since 2.0.0
 */

export var map = RR.map;
/**
 * @since 2.0.0
 */

export var reduceWithIndex = RR.reduceWithIndex;
/**
 * @since 2.0.0
 */

export var foldMapWithIndex = RR.foldMapWithIndex;
/**
 * @since 2.0.0
 */

export var reduceRightWithIndex = RR.reduceRightWithIndex;
/**
 * Create a `Record` with one key/value pair.
 *
 * @since 2.0.0
 */

export var singleton = RR.singleton;
export function traverseWithIndex(F) {
  return RR.traverseWithIndex(F);
}
export function traverse(F) {
  return RR.traverse(F);
}
export function sequence(F) {
  return RR.sequence(F);
}
/**
 * @category Witherable
 * @since 2.6.5
 */

export var wither = function (F) {
  var traverseF = traverse(F);
  return function (f) {
    return function (fa) {
      return F.map(pipe(fa, traverseF(f)), compact);
    };
  };
};
/**
 * @category Witherable
 * @since 2.6.5
 */

export var wilt = function (F) {
  var traverseF = traverse(F);
  return function (f) {
    return function (fa) {
      return F.map(pipe(fa, traverseF(f)), separate);
    };
  };
};
/**
 * @since 2.0.0
 */

export var partitionMapWithIndex = RR.partitionMapWithIndex;
export function partitionWithIndex(predicateWithIndex) {
  return RR.partitionWithIndex(predicateWithIndex);
}
/**
 * @since 2.0.0
 */

export var filterMapWithIndex = RR.filterMapWithIndex;
export function filterWithIndex(predicateWithIndex) {
  return RR.filterWithIndex(predicateWithIndex);
}
export function fromFoldable(M, F) {
  return RR.fromFoldable(M, F);
}
export function fromFoldableMap(M, F) {
  return RR.fromFoldableMap(M, F);
}
/**
 * @since 2.0.0
 */

export var every = RR.every;
/**
 * @since 2.0.0
 */

export var some = RR.some; // TODO: remove non-curried overloading in v3

/**
 * @since 2.0.0
 */

export var elem = RR.elem; // -------------------------------------------------------------------------------------
// non-pipeables
// -------------------------------------------------------------------------------------

/* istanbul ignore next */

var _map = function (fa, f) {
  return pipe(fa, map(f));
};
/* istanbul ignore next */


var _mapWithIndex = function (fa, f) {
  return pipe(fa, mapWithIndex(f));
};
/* istanbul ignore next */


var _reduce = function (fa, b, f) {
  return pipe(fa, reduce(b, f));
};
/* istanbul ignore next */


var _foldMap = function (M) {
  var foldMapM = foldMap(M);
  return function (fa, f) {
    return pipe(fa, foldMapM(f));
  };
};
/* istanbul ignore next */


var _reduceRight = function (fa, b, f) {
  return pipe(fa, reduceRight(b, f));
};
/* istanbul ignore next */


var _traverse = function (F) {
  var traverseF = traverse(F);
  return function (ta, f) {
    return pipe(ta, traverseF(f));
  };
};
/* istanbul ignore next */


var _filter = function (fa, predicate) {
  return pipe(fa, filter(predicate));
};
/* istanbul ignore next */


var _filterMap = function (fa, f) {
  return pipe(fa, filterMap(f));
};
/* istanbul ignore next */


var _partition = function (fa, predicate) {
  return pipe(fa, partition(predicate));
};
/* istanbul ignore next */


var _partitionMap = function (fa, f) {
  return pipe(fa, partitionMap(f));
};
/* istanbul ignore next */


var _reduceWithIndex = function (fa, b, f) {
  return pipe(fa, reduceWithIndex(b, f));
};
/* istanbul ignore next */


var _foldMapWithIndex = function (M) {
  var foldMapWithIndexM = foldMapWithIndex(M);
  return function (fa, f) {
    return pipe(fa, foldMapWithIndexM(f));
  };
};
/* istanbul ignore next */


var _reduceRightWithIndex = function (fa, b, f) {
  return pipe(fa, reduceRightWithIndex(b, f));
};
/* istanbul ignore next */


var _partitionMapWithIndex = function (fa, f) {
  return pipe(fa, partitionMapWithIndex(f));
};
/* istanbul ignore next */


var _partitionWithIndex = function (fa, predicateWithIndex) {
  return pipe(fa, partitionWithIndex(predicateWithIndex));
};
/* istanbul ignore next */


var _filterMapWithIndex = function (fa, f) {
  return pipe(fa, filterMapWithIndex(f));
};
/* istanbul ignore next */


var _filterWithIndex = function (fa, predicateWithIndex) {
  return pipe(fa, filterWithIndex(predicateWithIndex));
};
/* istanbul ignore next */


var _traverseWithIndex = function (F) {
  var traverseWithIndexF = traverseWithIndex(F);
  return function (ta, f) {
    return pipe(ta, traverseWithIndexF(f));
  };
};
/* istanbul ignore next */


var _wither = function (F) {
  var witherF = wither(F);
  return function (fa, f) {
    return pipe(fa, witherF(f));
  };
};
/* istanbul ignore next */


var _wilt = function (F) {
  var wiltF = wilt(F);
  return function (fa, f) {
    return pipe(fa, wiltF(f));
  };
}; // -------------------------------------------------------------------------------------
// type class members
// -------------------------------------------------------------------------------------

/**
 * @category Filterable
 * @since 2.0.0
 */


export var filter = RR.filter;
/**
 * @category Filterable
 * @since 2.0.0
 */

export var filterMap = RR.filterMap;
/**
 * @category Foldable
 * @since 2.0.0
 */

export var foldMap = RR.foldMap;
/**
 * @category Filterable
 * @since 2.0.0
 */

export var partition = RR.partition;
/**
 * @category Filterable
 * @since 2.0.0
 */

export var partitionMap = RR.partitionMap;
/**
 * @category Foldable
 * @since 2.0.0
 */

export var reduce = RR.reduce;
/**
 * @category Foldable
 * @since 2.0.0
 */

export var reduceRight = RR.reduceRight;
/**
 * @category Compactable
 * @since 2.0.0
 */

export var compact = RR.compact;
/**
 * @category Compactable
 * @since 2.0.0
 */

export var separate = RR.separate; // -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------

/**
 * @category instances
 * @since 2.0.0
 */

export var URI = 'Record';
/**
 * @category instances
 * @since 2.0.0
 */

export var getShow = RR.getShow;
/**
 * @category instances
 * @since 2.0.0
 */

export var getEq = RR.getEq;
/**
 * Returns a `Monoid` instance for `Record`s given a `Semigroup` instance for their values.
 *
 * @example
 * import { SemigroupSum } from 'fp-ts/number'
 * import { getMonoid } from 'fp-ts/Record'
 *
 * const M = getMonoid(SemigroupSum)
 * assert.deepStrictEqual(M.concat({ foo: 123 }, { foo: 456 }), { foo: 579 })
 *
 * @category instances
 * @since 2.0.0
 */

export var getMonoid = RR.getMonoid;
/**
 * @category instances
 * @since 2.7.0
 */

export var Functor = {
  URI: URI,
  map: _map
};
/**
 * Derivable from `Functor`.
 *
 * @category combinators
 * @since 2.10.0
 */

export var flap =
/*#_PURE_*/
flap_(Functor);
/**
 * @category instances
 * @since 2.7.0
 */

export var FunctorWithIndex = {
  URI: URI,
  map: _map,
  mapWithIndex: _mapWithIndex
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Foldable = {
  URI: URI,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight
};
/**
 * @category instances
 * @since 2.7.0
 */

export var FoldableWithIndex = {
  URI: URI,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  reduceWithIndex: _reduceWithIndex,
  foldMapWithIndex: _foldMapWithIndex,
  reduceRightWithIndex: _reduceRightWithIndex
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Compactable = {
  URI: URI,
  compact: compact,
  separate: separate
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Filterable = {
  URI: URI,
  map: _map,
  compact: compact,
  separate: separate,
  filter: _filter,
  filterMap: _filterMap,
  partition: _partition,
  partitionMap: _partitionMap
};
/**
 * @category instances
 * @since 2.7.0
 */

export var FilterableWithIndex = {
  URI: URI,
  map: _map,
  mapWithIndex: _mapWithIndex,
  compact: compact,
  separate: separate,
  filter: _filter,
  filterMap: _filterMap,
  partition: _partition,
  partitionMap: _partitionMap,
  filterMapWithIndex: _filterMapWithIndex,
  filterWithIndex: _filterWithIndex,
  partitionMapWithIndex: _partitionMapWithIndex,
  partitionWithIndex: _partitionWithIndex
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Traversable = {
  URI: URI,
  map: _map,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  traverse: _traverse,
  sequence: sequence
};
/**
 * @category instances
 * @since 2.7.0
 */

export var TraversableWithIndex = {
  URI: URI,
  map: _map,
  mapWithIndex: _mapWithIndex,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  reduceWithIndex: _reduceWithIndex,
  foldMapWithIndex: _foldMapWithIndex,
  reduceRightWithIndex: _reduceRightWithIndex,
  traverse: _traverse,
  sequence: sequence,
  traverseWithIndex: _traverseWithIndex
};
/**
 * @category instances
 * @since 2.7.0
 */

export var Witherable = {
  URI: URI,
  map: _map,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  traverse: _traverse,
  sequence: sequence,
  compact: compact,
  separate: separate,
  filter: _filter,
  filterMap: _filterMap,
  partition: _partition,
  partitionMap: _partitionMap,
  wither: _wither,
  wilt: _wilt
}; // -------------------------------------------------------------------------------------
// deprecated
// -------------------------------------------------------------------------------------

/**
 * Use a new `{}` instead.
 *
 * @since 2.0.0
 * @deprecated
 */

export var empty = {};
/**
 * Use [`upsertAt`](#upsertat) instead.
 *
 * @since 2.0.0
 * @deprecated
 */

export var insertAt = upsertAt;
/**
 * Use [`has`](#has) instead.
 *
 * @since 2.0.0
 * @deprecated
 */
// tslint:disable-next-line: deprecation

export var hasOwnProperty = RR.hasOwnProperty;
/**
 * Use small, specific instances instead.
 *
 * @category instances
 * @since 2.0.0
 * @deprecated
 */

export var record = {
  URI: URI,
  map: _map,
  reduce: _reduce,
  foldMap: _foldMap,
  reduceRight: _reduceRight,
  traverse: _traverse,
  sequence: sequence,
  compact: compact,
  separate: separate,
  filter: _filter,
  filterMap: _filterMap,
  partition: _partition,
  partitionMap: _partitionMap,
  mapWithIndex: _mapWithIndex,
  reduceWithIndex: _reduceWithIndex,
  foldMapWithIndex: _foldMapWithIndex,
  reduceRightWithIndex: _reduceRightWithIndex,
  filterMapWithIndex: _filterMapWithIndex,
  filterWithIndex: _filterWithIndex,
  partitionMapWithIndex: _partitionMapWithIndex,
  partitionWithIndex: _partitionWithIndex,
  traverseWithIndex: _traverseWithIndex,
  wither: _wither,
  wilt: _wilt
};